import { cloneDeep } from 'lodash';
import { formatISO, parseISO } from 'date-fns';
import AttachmentInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/attachment-interface';
import Client from '../client/client';
import ClientUser from '../client/client-user';
import JobCategory from './job-category';
import JobCode from './job-code';
import JobDto from './job-dto';
import JobStage from './job-stage';
import JobWorkflow from './job-workflow';
import Techspec from './techspec';
import User from '@vertical-plus/vue-js-micro-service-components/lib/model/entity/organisation/user';

export default class Job
{
    public dto!: JobDto;

    public constructor(dto: JobDto)
    {
        this.dto = Object.assign({}, dto);

        this._techspecId = this.dto.techspec ? this.dto.techspec.id : null;
        this._parentJobId = this.dto.parentJob ? this.dto.parentJob.id : null;
        this._clientId = this.dto.client ? this.dto.client.id : null;
        this._clientUserId = this.dto.clientUser ? this.dto.clientUser.id : null;
        this._creatorId = this.dto.creator ? this.dto.creator.id : null;
        this._assigneeId = this.dto.assignee ? this.dto.assignee.id : null;
        this._managerId = this.dto.manager ? this.dto.manager.id : null;
        this._stageId = this.dto.stage ? this.dto.stage.id : null;
        this._codeId = this.dto.code ? this.dto.code.id : null;
        this._workflowId = this.dto.workflow ? this.dto.workflow.id : null;
        this._categoryId = this.dto.category ? this.dto.category.id : null;
    }

    public get id(): number|null
    {
        return this.dto.id;
    }

    public set id(id: number|null)
    {
        this.dto.id = id;
    }

    public get techspec(): Techspec|null
    {
        if (!this.dto.techspec)
        {
            return null;
        }

        return new Techspec(this.dto.techspec);
    }

    private _techspecId: number|null = null;

    public get techspecId(): number|null
    {
        return this._techspecId;
    }

    public set techspecId(id: number|null)
    {
        this._techspecId = id;
    }

    public get parentJob(): Job|null
    {
        if (!this.dto.parentJob)
        {
            return null;
        }

        return new Job(this.dto.parentJob);
    }

    private _parentJobId: number|null = null;

    public get parentJobId(): number|null
    {
        return this._parentJobId;
    }

    public set parentJobId(id: number|null)
    {
        this._parentJobId = id;
    }

    public get client(): Client|null
    {
        if (!this.dto.client)
        {
            return null;
        }

        return new Client(this.dto.client);
    }

    private _clientId: number|null = null;

    public get clientId(): number|null
    {
        return this._clientId;
    }

    public set clientId(id: number|null)
    {
        this._clientId = id;
    }

    public get clientUser(): ClientUser|null
    {
        if (!this.dto.clientUser)
        {
            return null;
        }

        return new ClientUser(this.dto.clientUser);
    }

    private _clientUserId: number|null = null;

    public get clientUserId(): number|null
    {
        return this._clientUserId;
    }

    public set clientUserId(id: number|null)
    {
        this._clientUserId = id;
    }

    public get partnerUrgency(): string|null
    {
        return this.dto.partnerUrgency;
    }

    public set partnerUrgency(partnerUrgency: string|null)
    {
        this.dto.partnerUrgency = partnerUrgency;
    }

    public get partnerUrgencyDate(): Date|null
    {
        if (!this.dto.partnerUrgencyDate)
        {
            return null;
        }

        return parseISO(this.dto.partnerUrgencyDate);
    }

    public set partnerUrgencyDate(partnerUrgencyDate: Date|null)
    {
        if (!partnerUrgencyDate)
        {
            this.dto.partnerUrgencyDate = null;
            return;
        }

        this.dto.partnerUrgencyDate = formatISO(partnerUrgencyDate);
    }

    public get chasePartner(): boolean
    {
        return this.dto.chasePartner;
    }

    public set chasePartner(chasePartner: boolean)
    {
        this.dto.chasePartner = chasePartner;
    }

    public get chasePartnerDate(): Date|null
    {
        if (!this.dto.chasePartnerDate)
        {
            return null;
        }

        return parseISO(this.dto.chasePartnerDate);
    }

    public set chasePartnerDate(chasePartnerDate: Date|null)
    {
        if (!chasePartnerDate)
        {
            this.dto.chasePartnerDate = null;
            return;
        }

        this.dto.chasePartnerDate = formatISO(chasePartnerDate);
    }

    public get clientVisible(): boolean
    {
        return this.dto.clientVisible;
    }

    public set clientVisible(clientVisible: boolean)
    {
        this.dto.clientVisible = clientVisible;
    }

    public get type(): string
    {
        return this.dto.type;
    }

    public set type(type: string)
    {
        this.dto.type = type;
    }

    public get creator(): User|null
    {
        if (!this.dto.creator)
        {
            return null;
        }

        return new User(this.dto.creator);
    }

    private _creatorId: string|null = null;

    public get creatorId(): string|null
    {
        return this._creatorId;
    }

    public set creatorId(id: string|null)
    {
        this._creatorId = id;
    }

    public get assignee(): User|null
    {
        if (!this.dto.assignee)
        {
            return null;
        }

        return new User(this.dto.assignee);
    }

    private _assigneeId: string|null = null;

    public get assigneeId(): string|null
    {
        return this._assigneeId;
    }

    public set assigneeId(id: string|null)
    {
        this._assigneeId = id;
    }

    public get assignedDate(): Date|null
    {
        if (!this.dto.assignedDate)
        {
            return null;
        }

        return parseISO(this.dto.assignedDate);
    }

    public set assignedDate(assignedDate: Date|null)
    {
        if (!assignedDate)
        {
            this.dto.assignedDate = null;
            return;
        }

        this.dto.assignedDate = formatISO(assignedDate);
    }

    public get manager(): User|null
    {
        if (!this.dto.manager)
        {
            return null;
        }

        return new User(this.dto.manager);
    }

    private _managerId: string|null = null;

    public get managerId(): string|null
    {
        return this._managerId;
    }

    public set managerId(id: string|null)
    {
        this._managerId = id;
    }

    public get name(): string
    {
        return this.dto.name;
    }

    public set name(name: string)
    {
        this.dto.name = name;
    }

    public get description(): string|null
    {
        return this.dto.description;
    }

    public set description(description: string|null)
    {
        this.dto.description = description;
    }

    public get dateScheduled(): Date|null
    {
        if (!this.dto.dateScheduled)
        {
            return null;
        }

        return parseISO(this.dto.dateScheduled);
    }

    public set dateScheduled(dateScheduled: Date|null)
    {
        if (!dateScheduled)
        {
            this.dto.dateScheduled = null;
            return;
        }

        this.dto.dateScheduled = formatISO(dateScheduled);
    }

    public get startDate(): Date|null
    {
        if (!this.dto.startDate)
        {
            return null;
        }

        return parseISO(this.dto.startDate);
    }

    public set startDate(startDate: Date|null)
    {
        if (!startDate)
        {
            this.dto.startDate = null;
            return;
        }

        this.dto.startDate = formatISO(startDate);
    }

    public get deadline(): Date|null
    {
        if (!this.dto.deadline)
        {
            return null;
        }

        return parseISO(this.dto.deadline);
    }

    public set deadline(deadline: Date|null)
    {
        if (!deadline)
        {
            this.dto.deadline = null;
            return;
        }

        this.dto.deadline = formatISO(deadline);
    }

    public get targetEndDate(): Date|null
    {
        if (!this.dto.targetEndDate)
        {
            return null;
        }

        return parseISO(this.dto.targetEndDate);
    }

    public set targetEndDate(targetEndDate: Date|null)
    {
        if (!targetEndDate)
        {
            this.dto.targetEndDate = null;
            return;
        }

        this.dto.targetEndDate = formatISO(targetEndDate);
    }

    public get priority(): number
    {
        return this.dto.priority;
    }

    public set priority(priority: number)
    {
        this.dto.priority = priority;
    }

    public get stage(): JobStage|null
    {
        if (!this.dto.stage)
        {
            return null;
        }

        return new JobStage(this.dto.stage);
    }

    private _stageId: number|null = null;

    public get stageId(): number|null
    {
        return this._stageId;
    }

    public set stageId(id: number|null)
    {
        this._stageId = id;
    }

    public get stageEntryDate(): Date
    {
        return parseISO(this.dto.stageEntryDate);
    }

    public set stageEntryDate(stageEntryDate: Date)
    {
        this.dto.stageEntryDate = formatISO(stageEntryDate);
    }

    public get code(): JobCode|null
    {
        if (!this.dto.code)
        {
            return null;
        }

        return new JobCode(this.dto.code);
    }

    private _codeId: number|null = null;

    public get codeId(): number|null
    {
        return this._codeId;
    }

    public set codeId(id: number|null)
    {
        this._codeId = id;
    }

    public get workflow(): JobWorkflow|null
    {
        if (!this.dto.workflow)
        {
            return null;
        }

        return new JobWorkflow(this.dto.workflow);
    }

    private _workflowId: number|null = null;

    public get workflowId(): number|null
    {
        return this._workflowId;
    }

    public set workflowId(id: number|null)
    {
        this._workflowId = id;
    }

    public get amount(): string|null
    {
        return this.dto.amount;
    }

    public set amount(amount: string|null)
    {
        this.dto.amount = amount;
    }

    public get estimatedTime(): number|null
    {
        return this.dto.estimatedTime;
    }

    public set estimatedTime(estimatedTime: number|null)
    {
        this.dto.estimatedTime = estimatedTime;
    }

    public get estimatedTimeDesign(): number|null
    {
        return this.dto.estimatedTimeDesign;
    }

    public set estimatedTimeDesign(estimatedTimeDesign: number|null)
    {
        this.dto.estimatedTimeDesign = estimatedTimeDesign;
    }

    public get estimatedTimeTechnical(): number|null
    {
        return this.dto.estimatedTimeTechnical;
    }

    public set estimatedTimeTechnical(estimatedTimeTechnical: number|null)
    {
        this.dto.estimatedTimeTechnical = estimatedTimeTechnical;
    }

    public get hasAttachments(): boolean
    {
        return !!(this.attachment1 || this.attachment2 || this.attachment3 || this.attachment4 || this.attachment5);
    }

    public get attachment1(): AttachmentInterface|null
    {
        return this.dto.attachment1;
    }

    public set attachment1(attachment1: AttachmentInterface|null)
    {
        this.dto.attachment1 = attachment1;
    }

    public get attachment2(): AttachmentInterface|null
    {
        return this.dto.attachment2;
    }

    public set attachment2(attachment2: AttachmentInterface|null)
    {
        this.dto.attachment2 = attachment2;
    }

    public get attachment3(): AttachmentInterface|null
    {
        return this.dto.attachment3;
    }

    public set attachment3(attachment3: AttachmentInterface|null)
    {
        this.dto.attachment3 = attachment3;
    }

    public get attachment4(): AttachmentInterface|null
    {
        return this.dto.attachment4;
    }

    public set attachment4(attachment4: AttachmentInterface|null)
    {
        this.dto.attachment4 = attachment4;
    }

    public get attachment5(): AttachmentInterface|null
    {
        return this.dto.attachment5;
    }

    public set attachment5(attachment5: AttachmentInterface|null)
    {
        this.dto.attachment5 = attachment5;
    }

    public get githubBranch(): string|null
    {
        return this.dto.githubBranch;
    }

    public set githubBranch(githubBranch: string|null)
    {
        this.dto.githubBranch = githubBranch;
    }

    public get category(): JobCategory|null
    {
        if (!this.dto.category)
        {
            return null;
        }

        return new JobCategory(this.dto.category);
    }

    private _categoryId: number|null = null;

    public get categoryId(): number|null
    {
        return this._categoryId;
    }

    public set categoryId(id: number|null)
    {
        this._categoryId = id;
    }

    public get reason(): string|null
    {
        return this.dto.reason;
    }

    public set reason(reason: string|null)
    {
        this.dto.reason = reason;
    }

    public get customerSatisfactionLevel(): string|null
    {
        return this.dto.customerSatisfactionLevel;
    }

    public set customerSatisfactionLevel(customerSatisfactionLevel: string|null)
    {
        this.dto.customerSatisfactionLevel = customerSatisfactionLevel;
    }

    public get roiLevel(): string|null
    {
        return this.dto.roiLevel;
    }

    public set roiLevel(roiLevel: string|null)
    {
        this.dto.roiLevel = roiLevel;
    }

    public get roiAmount(): string|null
    {
        return this.dto.roiAmount;
    }

    public set roiAmount(roiAmount: string|null)
    {
        this.dto.roiAmount = roiAmount;
    }

    public get tags(): string[]
    {
        return this.dto.tags;
    }

    public set tags(tags: string[])
    {
        this.dto.tags = tags;
    }

    public get contracted(): boolean
    {
        return this.dto.contracted;
    }

    public set contracted(contracted: boolean)
    {
        this.dto.contracted = contracted;
    }

    public get quickJob(): boolean
    {
        return this.dto.quickJob;
    }

    public set quickJob(quickJob: boolean)
    {
        this.dto.quickJob = quickJob;
    }

    public get ticketTypeId(): number
    {
        return this.dto.ticketTypeId;
    }

    public set ticketTypeId(ticketTypeId: number)
    {
        this.dto.ticketTypeId = ticketTypeId;
    }

    public get order(): number
    {
        return this.dto.order;
    }

    public set order(order: number)
    {
        this.dto.order = order;
    }

    public get jiraLink(): string|null
    {
        return this.dto.jiraLink;
    }

    public set jiraLink(jiraLink: string|null)
    {
        this.dto.jiraLink = jiraLink;
    }

    public get dateCreated(): Date
    {
        return parseISO(this.dto.dateCreated);
    }

    public get dateUpdated(): Date
    {
        return parseISO(this.dto.dateUpdated);
    }

    public get ticket(): string|null
    {
        if (this.workflowId === 28 && this.partnerUrgency)
        {
            return this.partnerUrgency;
        }
        else
        {
            return null;
        }
    }

    public set ticket(ticket: string|null)
    {
        switch (ticket)
        {
        case 'emergency_same_day':
            this.workflowId = 28;
            this.partnerUrgency = 'emergency_same_day';
            break;

        case 'emergency_seven_days':
            this.workflowId = 28;
            this.partnerUrgency = 'emergency_seven_days';
            break;

        case 'no':
        default:
            this.workflowId = null;
            this.partnerUrgency = null;
            break;
        }
    }

    public get childJobs(): Job[]
    {
        const childJobs: Job[] = [];

        for (const childJobDto of this.dto.childJobs)
        {
            childJobs.push(new Job(childJobDto));
        }

        return childJobs;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public get params(): {[key: string]: any}
    {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dto: any = cloneDeep(this.dto);

        dto.techspec = this.techspecId ? `/techspecs/${ this.techspecId }` : null;
        dto.parentJob = this.parentJobId ? `/jobs/${ this.parentJobId }` : null;
        dto.client = this.clientId ? `/clients/${ this.clientId }` : null;
        dto.clientUser = this.clientUserId ? `/client_users/${ this.clientUserId }` : null;
        dto.creator = this.creatorId ? `/users/${ this.creatorId }` : null;
        dto.assignee = this.assigneeId ? `/users/${ this.assigneeId }` : null;
        dto.manager = this.managerId ? `/users/${ this.managerId }` : null;
        dto.stage = this.stageId ? `/job_stages/${ this.stageId }` : null;
        dto.code = this.codeId ? `/job_codes/${ this.codeId }` : null;
        dto.workflow = this.workflowId ? `/job_workflows/${ this.workflowId }` : null;
        dto.category = this.categoryId ? `/job_categories/${ this.categoryId }` : null;

        return dto;
    }
}